import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';
import ReactMarkdown from 'react-markdown';

interface LegalModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  markdown: string;
}

/** Strip the first line if it's a single # heading, so we don't duplicate the modal title in the body. */
function stripLeadingH1(md: string): string {
  const trimmed = md.trimStart();
  const match = trimmed.match(/^# [^\n]+\n+/);
  return match ? trimmed.slice(match[0].length) : trimmed;
}

const LegalModal: React.FC<LegalModalProps> = ({ open, onClose, title, markdown }) => {
  const overlayRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const bodyMarkdown = stripLeadingH1(markdown);

  useEffect(() => {
    if (!open) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';
    closeButtonRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === overlayRef.current) onClose();
  };

  const modalContent = (
    <div
      ref={overlayRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="legal-modal-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={handleOverlayClick}
    >
      <div
        className="bg-gray-900 border border-white/10 rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between flex-shrink-0 px-4 sm:px-6 py-4 border-b border-white/10">
          <h2 id="legal-modal-title" className="text-lg sm:text-xl font-semibold text-white">
            {title}
          </h2>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 focus:outline-none transition-colors"
            aria-label="Close"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar px-4 sm:px-6 py-4 sm:py-6">
          <article className="legal-content text-gray-300 text-sm sm:text-base leading-relaxed">
            <ReactMarkdown
              components={{
                h1: ({ node, children, ...props }) => <h1 className="text-xl sm:text-2xl font-bold text-white mb-4 mt-0" {...props}>{children}</h1>,
                h2: ({ node, children, ...props }) => <h2 className="text-base sm:text-lg font-semibold text-white mt-3 mb-2 border-b border-white/10 pb-2" {...props}>{children}</h2>,
                p: ({ node, ...props }) => <p className="mb-3" {...props} />,
                ul: ({ node, ...props }) => <ul className="list-disc pl-6 mb-3 space-y-1" {...props} />,
                li: ({ node, ...props }) => <li className="leading-relaxed" {...props} />,
                hr: ({ node, ...props }) => <hr className="border-white/10 my-2" {...props} />,
                a: ({ node, href, children, ...props }) => (
                  <a href={href} target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:text-cyan-300 underline" {...props}>{children}</a>
                ),
              }}
            >
              {bodyMarkdown}
            </ReactMarkdown>
          </article>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default LegalModal;
